/**
 * PURPOSE: Traducir los errores del Centro de Programación a algo accionable (FTR-0028 WG3, §22).
 * RESPONSIBILITIES: Leer el sobre V2 (`{ ok:false, code, message, details }`) del error de axios y
 *   devolver `{ code, status, message, details, retryable, requiresReload }`. Puro.
 *
 *   EL MENSAJE DEL BACKEND GANA cuando existe: está escrito para el operador y suele traer el dato
 *   concreto (qué fila, qué formato). El diccionario de aquí solo cubre los códigos donde la interfaz
 *   sabe algo que el backend no puede saber — qué hacer a continuación.
 *
 *   LOS 409 NUNCA SE REINTENTAN SOLOS. `DRAFT_VERSION_CONFLICT`, `SYNC_SUMMARY_STALE` y
 *   `REORGANIZE_PLAN_STALE` significan que lo que el administrador aprobó ya no es lo que pasaría.
 *   Reintentar en silencio aplicaría algo que nadie vio. Se recarga y se vuelve a revisar.
 * COLLABORATORS: programmingCenterService.js, ProgrammingCenterModal.jsx y sus paneles.
 * ROUTE: winscore/src/utils/programmingCenterErrors.js
 */

export const PROGRAMMING_CENTER_ERROR_MESSAGES = Object.freeze({
  PROGRAMMING_CENTER_DISABLED:
    "El Centro de Programación no está habilitado en este servidor.",
  SESSION_REQUIRED: "Tu sesión expiró. Vuelve a iniciar sesión para continuar.",
  USER_NOT_ENABLED: "Tu usuario no está habilitado.",
  ROLE_NOT_ALLOWED: "Tu rol no tiene acceso al Centro de Programación.",
  EVENT_ACCESS_DENIED: "No tienes acceso a este evento.",
  EVENT_NOT_FOUND: "El evento no existe.",
  SPORT_CONTEXT_NOT_FOUND: "El deporte no tiene configuración en este evento.",
  EVENT_CONTEXT_INVALID: "Falta el evento o el deporte de la solicitud.",
  SPORT_NOT_COLLECTIVE: "El Centro de Programación solo trabaja con deportes colectivos.",
  CONFIG_CLOSED:
    "La configuración del deporte está cerrada. Ábrela en Configuración para poder modificar el fixture.",

  DRAFT_NOT_FOUND: "Todavía no hay un fixture armado para este deporte.",
  DRAFT_MATCH_NOT_FOUND: "La fila del fixture ya no existe. Actualiza la vista.",
  MATCH_NOT_FOUND: "El partido ya no existe o fue eliminado.",
  MATCH_NOT_OWNED:
    "Ese partido no fue creado por el Centro de Programación, así que el Centro no lo modifica.",
  MATCH_HAS_ACTIVITY:
    "El partido ya tiene actividad competitiva: su estructura y sus participantes no se tocan.",

  DRAFT_VERSION_CONFLICT:
    "Otra persona modificó el fixture. Actualiza la vista y vuelve a intentarlo.",
  SYNC_REQUIRED:
    "Esta configuración haría desaparecer filas existentes. Revisa el impacto antes de aplicarla.",
  SYNC_SUMMARY_STALE:
    "El impacto cambió desde que lo viste. Actualiza la vista y vuelve a revisarlo antes de aplicar.",
  REORGANIZE_PLAN_STALE:
    "El plan cambió. Actualiza la vista y vuelve a revisarlo antes de ejecutar.",

  GROUP_LIMIT_EXCEEDED: "La distribución necesita más de 10 grupos, que es el máximo.",
  REFERENCE_INVALID: "La referencia del partido no es válida.",
  DRAFT_CONFIG_INVALID: "Revisa los datos del fixture: alguno no es válido.",
  TEMPLATE_NOT_AVAILABLE:
    "No hay plantilla de Fixture Base para alguno de los tamaños de grupo requeridos.",
  DRAFT_ROW_MATERIALIZED:
    "La fila ya tiene un partido real: para quitarla hay que retirar el partido.",
  DRAFT_ROW_NOT_ACTIVE:
    "La fila está huérfana y su programación se conserva por si su configuración vuelve.",
  BYE_NOT_SCHEDULABLE: "Un BYE no se programa: no se juega.",
  SCHEDULE_INVALID: "Revisa los datos de programación del lote.",

  VENUE_INVALID: "La sede no está disponible para este evento.",
  COURT_NOT_IN_VENUE: "La cancha no pertenece a la sede seleccionada o está inactiva.",
  COURT_INVALID: "El nombre de la cancha no es válido.",
  COURT_NAME_TAKEN: "Esa sede ya tiene una cancha con ese nombre.",
  COURT_NOT_FOUND: "La cancha no existe en esa sede.",

  SLOT_TAKEN: "Ese slot ya está ocupado por otra institución.",
  SLOT_OUT_OF_RANGE: "Ese slot no existe en la distribución actual de grupos.",
  PARTICIPANT_NOT_FOUND: "La institución no está inscrita en este evento y deporte.",
});

/** Conflictos de concurrencia: obligan a recargar y volver a aprobar, nunca a reintentar solos. */
export const RELOAD_REQUIRED_CODES = Object.freeze([
  "DRAFT_VERSION_CONFLICT",
  "SYNC_SUMMARY_STALE",
  "REORGANIZE_PLAN_STALE",
  "SYNC_REQUIRED",
]);

const FALLBACK_BY_STATUS = {
  400: "La solicitud no es válida.",
  401: "Tu sesión expiró. Vuelve a iniciar sesión.",
  403: "No tienes permiso para esta acción.",
  404: "El recurso no existe o no está disponible.",
  409: "El estado cambió mientras trabajabas. Actualiza la vista.",
};

/**
 * Normaliza cualquier fallo — sobre V2, error HTTP sin cuerpo, red caída — a la misma forma. Nunca
 * lanza: una vista que renderiza un error no puede romperse al renderizarlo.
 */
export const parseProgrammingCenterError = (error) => {
  const response = error?.response;
  const body = response?.data || null;
  const status = Number(response?.status) || null;
  const code = typeof body?.code === "string" ? body.code : null;
  const details = body?.details && typeof body.details === "object" ? body.details : null;

  const message =
    (typeof body?.message === "string" && body.message.trim()) ||
    (code && PROGRAMMING_CENTER_ERROR_MESSAGES[code]) ||
    (status && FALLBACK_BY_STATUS[status]) ||
    (error?.message === "Network Error"
      ? "No se pudo conectar con el servidor."
      : "Ocurrió un error inesperado.");

  return {
    code,
    status,
    message,
    details,
    requiresReload: Boolean(code && RELOAD_REQUIRED_CODES.includes(code)),
    // 401 lo gestiona AuthSessionBridge; 403/404 son decisiones del servidor, no fallos pasajeros.
    retryable: status !== null && status >= 500,
  };
};

/** Texto listo para un toast. Antepone el contexto de la acción cuando se da. */
export const programmingCenterErrorMessage = (error, prefix = "") => {
  const parsed = parseProgrammingCenterError(error);
  return prefix ? `${prefix}: ${parsed.message}` : parsed.message;
};

/** Estado vacío de la pantalla: distingue "no hay fixture" de "no tienes acceso" de "está apagado". */
export const classifyAccessError = (error) => {
  const parsed = parseProgrammingCenterError(error);
  if (parsed.code === "DRAFT_NOT_FOUND") return "no-draft";
  if (parsed.code === "PROGRAMMING_CENTER_DISABLED" || parsed.status === 404) return "unavailable";
  if (parsed.status === 401) return "session";
  if (parsed.status === 403) return "forbidden";
  return "error";
};
